<script lang="ts">
  import type { ClinicalGroupSummary } from '$lib/api/types';
  import FormModal from '../common/modal/FormModal.svelte';
  import { listInstructors } from '$lib/api/users';
  import QuickSearch from '../common/QuickSearch.svelte';
  import { createClinicalGroup, deleteClinicalGroup, updateClinicalGroup } from '$lib/api/classes';
  import Label from '../common/bootstrap/Label.svelte';
  import Input from '../common/bootstrap/Input.svelte';

  interface Props {
    classId: number;
    clinicalGroup: ClinicalGroupSummary | null;
    onClose: () => void;
  }

  let { classId, clinicalGroup, onClose }: Props = $props();
  let name = $state(clinicalGroup?.name ?? '');
  let instructorValue = $state(clinicalGroup?.instructorId?.toString() ?? null);
  let nameTouched = $state(false);
  let instructorTouched = $state(false);

  let instructorInvalid = $derived(instructorTouched && (instructorValue === null || instructorValue.length === 0));
</script>

<FormModal
  formId="clinical_group_editor"
  invalidateUrl={url => url.pathname.startsWith('/api/classes')}
  header="Clinical Group"
  onDelete={clinicalGroup
    ? async () => {
        await deleteClinicalGroup(classId, clinicalGroup!.id);
      }
    : undefined}
  deleteConfirmText="Delete clinical group? Students in this clinical group will not be in any clinical group after this operation is complete."
  onclose={onClose}
  onsubmit={async () => {
    if (clinicalGroup) {
      await updateClinicalGroup(classId, clinicalGroup!.id, { name, instructorId: parseInt(instructorValue!) });
    } else {
      await createClinicalGroup(classId, { name, instructorId: parseInt(instructorValue!) });
    }
    return true;
  }}
  validationChecks={() => {
    nameTouched = true;
    instructorTouched = true;
    return name.length > 0 && name.length <= 128 && instructorValue !== null;
  }}
>
  <div class="mb-3">
    <Label for="clinical_group_name" class="is-required">Name</Label>
    <Input
      bind:value={name}
      required
      maxlength={128}
      type="text"
      id="clinical_group_name"
      placeholder="e.g. Clinical Group A"
      invalid={nameTouched && (name.length === 0 || name.length > 128)}
      onblur={() => (nameTouched = true)}
      feedback="Clinical Group Name is required and must be 128 characters or less"
    />
  </div>
  <div class="mb-3">
    <Label for="instructor_id" class="is-required">Instructor</Label>
    <QuickSearch
      placeholder="Select Instructor"
      id="instructor_id"
      bind:value={instructorValue}
      invalid={instructorInvalid}
      feedback="Please choose an instructor."
      valueText={clinicalGroup && clinicalGroup.instructorFirstName ? `${clinicalGroup.instructorFirstName} ${clinicalGroup.instructorLastName}` : ''}
      minimumQueryLength={0}
      loadData={async (query, page, pageSize, signal) => {
        const users = await listInstructors(
          // show full list instead of search query if a value is set
          instructorValue !== null && instructorValue.length > 0 ? '' : `search=${query}`,
          page,
          pageSize,
          signal,
        );
        return users.map(u => {
          return {
            value: u.id.toString(),
            text: `${u.firstName} ${u.lastName}`,
            title: `${u.firstName} ${u.lastName}`,
          };
        });
      }}
      bind:touched={instructorTouched}
      emptySearchResultsLabel="No instructors found"
    />
  </div>
</FormModal>
